// ProductListPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, TextField, TableSortLabel, useMediaQuery
} from '@mui/material';
import { Product } from '../../types/ProductsForm';
import { apiRequest } from '../../utils/apiClient';
import AddProduct from './AddProduct';
import { useNavigate } from 'react-router-dom';
import { useVendorContext } from '../../context/VendorContext';
import { useTheme } from '@mui/material/styles';

type Order = 'asc' | 'desc';

const ProductListPage = ({ initialCategory }: { initialCategory?: string }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Product>('productName');
  const navigator = useNavigate();
  const { vendor } = useVendorContext();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchProducts = async () => {

      try {
        setLoading(true);
        const response = await apiRequest(`/products/${vendor.vendorName}`);
        const filtered = initialCategory
          ? response.filter((p: Product) => !initialCategory || p.category === initialCategory)
          : response;
        setProducts(filtered);
        setFilteredProducts(filtered);
      } catch (error) {
        console.error("Error fetching products:", error);
      } 
      finally {
        setLoading(false);
      }
    };


    setLoading(true);
    fetchProducts();
  }, [initialCategory]);

  useEffect(() => {
    const filtered = products.filter((p) =>
      p.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.productCode.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  const handleDelete = async (productId?: string) => {
    if (!productId || productId === 'undefined') {
      console.error('Delete skipped: invalid product id', productId);
      return;
    }

    try {
      const res = await apiRequest(`/products/${productId}`, { method: 'DELETE' });
      if (res) {
        setProducts(prev => prev.filter(p => (p.productId ?? p.id?.toString()) !== productId));
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setEditDialogOpen(true);
  };

  const handleRequestSort = (property: keyof Product) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    const sorted = [...filteredProducts].sort((a, b) => {
      const valA = a[property] ?? '';
      const valB = b[property] ?? '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });
    setFilteredProducts(sorted);
  };

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>Product List</Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        mb={2}
      >
        <Button variant="contained" onClick={() => navigator('/add-product')}>
          Add New Product
        </Button>
        <TextField
          label="Search by Name or Code"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ maxWidth: { xs: '100%', md: 340 } }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {filteredProducts.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              No products found.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                Total Products: {filteredProducts.length}
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Image</TableCell>
                      <TableCell sortDirection={orderBy === 'productCode' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'productCode'}
                          direction={orderBy === 'productCode' ? order : 'asc'}
                          onClick={() => handleRequestSort('productCode')}
                        >
                          Product Code
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'productName' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'productName'}
                          direction={orderBy === 'productName' ? order : 'asc'}
                          onClick={() => handleRequestSort('productName')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'category' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'category'}
                          direction={orderBy === 'category' ? order : 'asc'}
                          onClick={() => handleRequestSort('category')}
                        >
                          Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'price' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'price'}
                          direction={orderBy === 'price' ? order : 'asc'}
                          onClick={() => handleRequestSort('price')}
                        >
                          Price
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProducts.map((product) => {
                      const resolvedId = product.productId ?? product.id?.toString();
                      return (
                      <TableRow key={resolvedId ?? `${product.productCode}-${product.productName}`}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {product.image && product.image.length > 0 && (
                              <img
                                src={product.image[0]}
                                alt={product.productName}
                                style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{product.productCode}</TableCell>
                        <TableCell>{product.productName}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>₹{product.price.toLocaleString()}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Button size="small" variant="outlined" onClick={() => handleEdit(product)}>Edit</Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(resolvedId)}>Delete</Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Product</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1, md: 2 } }}>
          {selectedProduct && (
            <AddProduct
              updateproduct={selectedProduct}
              isEditMode={true}
              isAddMode={false}
              openDialog={setEditDialogOpen}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductListPage;
